// src/components/HeatMap.js
import React, { useEffect, useRef, useState } from 'react';
import { firestore, collection, query, where, onSnapshot } from '../firebase-config';
import './HeatMap.css';

const HeatMap = () => {
  const canvasRef = useRef(null);
  const [blocks, setBlocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [heatStats, setHeatStats] = useState({ max: 0, min: 0, average: 0, total: 0 });

  // Define map bounds (adjust these based on your server)
  const MAP_BOUNDS = {
    minX: -100,
    maxX: 100,
    minZ: -100,
    maxZ: 100
  };

  const PIXEL_SIZE = 4; // Each block = 4x4 pixels on canvas
  const MAP_WIDTH = MAP_BOUNDS.maxX - MAP_BOUNDS.minX;
  const MAP_HEIGHT = MAP_BOUNDS.maxZ - MAP_BOUNDS.minZ;

  useEffect(() => {
    // Query blocks within map area
    const blocksRef = collection(firestore, 'blocks');
    const q = query(
      blocksRef,
      where('x', '>=', MAP_BOUNDS.minX),
      where('x', '<=', MAP_BOUNDS.maxX)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newBlocks = {};
      let maxHeat = 0;
      let minHeat = Infinity;
      let totalHeat = 0;
      let heatedBlocks = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        // Filter Z coordinate client-side
        if (data.z >= MAP_BOUNDS.minZ && data.z <= MAP_BOUNDS.maxZ) {
          const key = `${data.x}_${data.z}`;
          const heat = data.heat || 0;
          
          newBlocks[key] = {
            x: data.x,
            z: data.z,
            heat: heat,
            colour: data.colour || 'WHITE',
            lastPainter: data.lastPainter
          };

          if (heat > 0) {
            maxHeat = Math.max(maxHeat, heat);
            minHeat = Math.min(minHeat, heat);
            totalHeat += heat;
            heatedBlocks++;
          }
        }
      });

      setBlocks(newBlocks);
      setHeatStats({
        max: maxHeat,
        min: minHeat === Infinity ? 0 : minHeat,
        average: heatedBlocks > 0 ? (totalHeat / heatedBlocks).toFixed(1) : 0,
        total: Object.keys(newBlocks).length
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Convert heat value to color (blue → cyan → green → yellow → orange → red)
  const getHeatColor = (heat) => {
    if (heat === 0) return '#F0F0F0'; // Light grey for unpainted/no heat

    // Normalize heat to 0-1 scale (cap at max for better visualization)
    const maxHeat = Math.max(heatStats.max, 50); // Cap visualization at 50 for better gradient
    const normalized = Math.min(heat / maxHeat, 1);

    // Color stops for heat gradient
    if (normalized < 0.2) {
      // Blue → Cyan (0-20%)
      const t = normalized / 0.2;
      return interpolateColor('#0000FF', '#00FFFF', t);
    } else if (normalized < 0.4) {
      // Cyan → Green (20-40%)
      const t = (normalized - 0.2) / 0.2;
      return interpolateColor('#00FFFF', '#00FF00', t);
    } else if (normalized < 0.6) {
      // Green → Yellow (40-60%)
      const t = (normalized - 0.4) / 0.2;
      return interpolateColor('#00FF00', '#FFFF00', t);
    } else if (normalized < 0.8) {
      // Yellow → Orange (60-80%)
      const t = (normalized - 0.6) / 0.2;
      return interpolateColor('#FFFF00', '#FF8800', t);
    } else {
      // Orange → Red (80-100%)
      const t = (normalized - 0.8) / 0.2;
      return interpolateColor('#FF8800', '#FF0000', t);
    }
  };

  // Interpolate between two hex colors
  const interpolateColor = (color1, color2, factor) => {
    const c1 = parseInt(color1.slice(1), 16);
    const c2 = parseInt(color2.slice(1), 16);
    
    const r1 = (c1 >> 16) & 0xff;
    const g1 = (c1 >> 8) & 0xff;
    const b1 = c1 & 0xff;
    
    const r2 = (c2 >> 16) & 0xff;
    const g2 = (c2 >> 8) & 0xff;
    const b2 = c2 & 0xff;
    
    const r = Math.round(r1 + factor * (r2 - r1));
    const g = Math.round(g1 + factor * (g2 - g1));
    const b = Math.round(b1 + factor * (b2 - b1));
    
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = MAP_WIDTH * PIXEL_SIZE;
    const height = MAP_HEIGHT * PIXEL_SIZE;

    canvas.width = width;
    canvas.height = height;

    // Clear canvas with light grey background
    ctx.fillStyle = '#F0F0F0';
    ctx.fillRect(0, 0, width, height);

    // Draw grid lines (very subtle)
    ctx.strokeStyle = '#E0E0E0';
    ctx.lineWidth = 0.5;
    for (let x = 0; x <= MAP_WIDTH; x += 10) {
      ctx.beginPath();
      ctx.moveTo(x * PIXEL_SIZE, 0);
      ctx.lineTo(x * PIXEL_SIZE, height);
      ctx.stroke();
    }
    for (let z = 0; z <= MAP_HEIGHT; z += 10) {
      ctx.beginPath();
      ctx.moveTo(0, z * PIXEL_SIZE);
      ctx.lineTo(width, z * PIXEL_SIZE);
      ctx.stroke();
    }

    // Draw heat map
    Object.values(blocks).forEach(block => {
      const canvasX = (block.x - MAP_BOUNDS.minX) * PIXEL_SIZE;
      const canvasZ = (block.z - MAP_BOUNDS.minZ) * PIXEL_SIZE;

      ctx.fillStyle = getHeatColor(block.heat);
      ctx.fillRect(canvasX, canvasZ, PIXEL_SIZE, PIXEL_SIZE);
    });

  }, [blocks, heatStats]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;

    const blockX = Math.floor(clickX / PIXEL_SIZE) + MAP_BOUNDS.minX;
    const blockZ = Math.floor(clickY / PIXEL_SIZE) + MAP_BOUNDS.minZ;
    const key = `${blockX}_${blockZ}`;

    const block = blocks[key];
    if (block) {
      const heatLevel = 
        block.heat === 0 ? 'Cold (no activity)' :
        block.heat < 5 ? 'Cool (low activity)' :
        block.heat < 10 ? 'Warm (moderate activity)' :
        block.heat < 20 ? 'Hot (high activity)' :
        block.heat < 50 ? 'Very Hot (very high activity)' :
        'Off the charts (extreme activity)';

      alert(
        `Block at (${blockX}, ${blockZ})\n` +
        `Painted over ${block.heat} times\n` +
        `Status: ${heatLevel}\n` +
        `Current Color: ${block.colour}\n` +
        `Last painted by: ${block.lastPainter || 'Unknown'}`
      );
    } else {
      alert(`Block at (${blockX}, ${blockZ})\nNot painted yet.`);
    }
  };

  return (
    <div className="heat-map">
      <div className="map-header">
        <h3>Canvas Heat Map</h3>
        <div className="heat-stats">
          <span className="heat-stat">
            <strong>Max:</strong> {heatStats.max}
          </span>
          <span className="heat-stat">
            <strong>Avg:</strong> {heatStats.average}
          </span>
          <span className="heat-stat">
            <strong>Blocks:</strong> {heatStats.total}
          </span>
        </div>
      </div>
      {loading ? (
        <div className="map-loading">Loading heat map...</div>
      ) : (
        <>
          <div className="map-container">
            <canvas 
              ref={canvasRef}
              onClick={handleCanvasClick}
              style={{ cursor: 'crosshair' }}
            />
          </div>
          <div className="heat-legend">
            <div className="legend-title">Heat Intensity</div>
            <div className="legend-gradient">
              <div className="gradient-bar" />
              <div className="gradient-labels">
                <span>0 (Cold)</span>
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
                <span>{heatStats.max}+ (Hot)</span>
              </div>
            </div>
            <small>Click a block to see heat details • Darker areas = more contested</small>
          </div>
        </>
      )}
    </div>
  );
};

export default HeatMap;